import React from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@features/auth';
import { CheckCircle, Clock, ShieldCheck, AlertCircle } from 'lucide-react';

import styles from './ComplianceSidebar.module.css';

import ProgressBar from '@/shared/ui/ProgressBar/ProgressBar';
import { OHIO_COMPLIANCE } from '@/shared/constants';
import { CourseProgress } from '../../api/progressServices';

import { ComplianceGauge } from '@/shared/ui';

export interface ComplianceSidebarProps {
    progress?: CourseProgress;
    totalTimeDailySeconds: number;
    sessionTimeSeconds: number;
    totalCourseTimeSeconds?: number;
    timeUntilBreak: number;
    currentModuleProgress?: number;
}

const ComplianceSidebar: React.FC<ComplianceSidebarProps> = ({
    progress,
    totalTimeDailySeconds,
    sessionTimeSeconds,
    totalCourseTimeSeconds,
    timeUntilBreak,
    currentModuleProgress = 0
}) => {
    const { courseId } = useParams<{ courseId: string }>();
    const { user } = useAuth();
    const module1LessonsComplete = ['lesson-1-1', 'lesson-1-2', 'lesson-1-3', 'lesson-1-4'].every(
        lessonId => progress?.lessonProgress?.[lessonId]?.completed === true
    );
    const m1Complete = progress?.moduleProgress?.['module-1']?.completed === true || module1LessonsComplete;

    const module2LessonsComplete = ['lesson-2-1', 'lesson-2-2', 'lesson-2-3', 'lesson-2-4', 'lesson-2-5', 'lesson-2-6', 'lesson-2-7'].every(
        lessonId => progress?.lessonProgress?.[lessonId]?.completed === true
    );
    const m2Complete = progress?.moduleProgress?.['module-2']?.completed === true || module2LessonsComplete;

    const modules1And2Complete = m1Complete && m2Complete;

    const totalSeconds = totalCourseTimeSeconds || progress?.totalInstructionSeconds || 0;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const dailyMinutes = Math.floor(totalTimeDailySeconds / 60);

    const totalRequirement = OHIO_COMPLIANCE.TIME_REQUIREMENTS.TOTAL_INSTRUCTION_MINUTES;
    const dailyRequirement = OHIO_COMPLIANCE.TIME_REQUIREMENTS.DAILY_LIMIT_MINUTES;

    const totalPercent = Math.min(100, Math.round((totalMinutes / totalRequirement) * 100));

    const finalExamPassed = progress?.lessonProgress?.['lesson-final-test']?.completed === true;
    const isCompletionEligible = totalMinutes >= totalRequirement && 
                                 progress?.overallProgress === 100 && 
                                 finalExamPassed;

    // Calculate mandatory break countdown (triggered every 120 mins)
    const minutesUntilBreak = Math.max(0, Math.ceil(timeUntilBreak / 60));
    
    // Switch to daily lockout countdown once the first mandatory break (120m mark) is complete.
    // If minutesUntilBreak + dailyMinutes > 120, the break has been completed (resets to 120).
    const hasCompletedBreak = dailyMinutes >= 120 || (minutesUntilBreak + dailyMinutes > 120);
    const minutesUntilLockout = Math.max(0, dailyRequirement - dailyMinutes);

    const formatTimeShort = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    return (
        <div className={styles.container}>
            {/* Daily Progress Section */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <ShieldCheck className={styles.icon} size={18} />
                    <h4>Daily Progress</h4>
                </div>

                <div className={styles.gaugeWrapper}>
                    <ComplianceGauge
                        value={dailyMinutes}
                        max={dailyRequirement}
                        label="Daily Limit"
                        subLabel={`${dailyMinutes}/${dailyRequirement}m`}
                        color={dailyMinutes >= dailyRequirement * 0.9 ? 'var(--color-warning)' : 'var(--brand-action)'}
                    />
                </div>

                {/* Daily Lockout Countdown - Always Visible */}
                <div className={styles.breakCountdown}>
                    <div className={styles.countdownHeader}>
                        <AlertCircle size={14} className={minutesUntilLockout <= 15 ? styles.warningIcon : ''} />
                        <span>Daily Limit Lockout in</span>
                    </div>
                    <div className={styles.countdownValue}>
                        {formatTimeShort(minutesUntilLockout)}
                    </div>
                </div>

                {/* Mandatory Break Countdown - Visible only if break not completed */}
                {!hasCompletedBreak && (
                    <div className={styles.breakCountdown} style={{ marginTop: '12px' }}>
                        <div className={styles.countdownHeader}>
                            <AlertCircle size={14} className={minutesUntilBreak <= 15 ? styles.warningIcon : ''} />
                            <span>Next Mandatory Break in</span>
                        </div>
                        <div className={styles.countdownValue}>
                            {formatTimeShort(minutesUntilBreak)}
                        </div>
                    </div>
                )}

                {/* Current Module Progress */}
                <div className={styles.statGroup}>
                    <div className={styles.statLabel}>
                        <span>Current Module</span>
                        <span className={styles.statValue}>{currentModuleProgress}%</span>
                    </div>
                    <ProgressBar progress={currentModuleProgress} size="small" color="primary" />
                </div>

                <div className={styles.checklistSection}>
                    <div className={`${styles.checklistHeader} ${totalMinutes >= 120 && modules1And2Complete ? styles.headerComplete : styles.headerIncomplete}`}>
                        <ShieldCheck className={styles.icon} size={14} />
                        <h5>Enrollment Certificate Eligibility</h5>
                    </div>
                    <ul className={styles.checklist}>
                        <li className={totalMinutes >= 120 ? styles.itemComplete : styles.itemIncomplete}>
                            <CheckCircle size={14} />
                            <span>2h Instruction Time</span>
                        </li>
                        <li className={modules1And2Complete ? styles.itemComplete : styles.itemIncomplete}>
                            <CheckCircle size={14} />
                            <span>Modules 1 & 2 Completed</span>
                        </li>
                    </ul>
                </div>
            </section>

            {/* Overall Progress Section */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <ShieldCheck className={styles.icon} size={18} />
                    <h4>Overall Progress</h4>
                </div>

                <div className={styles.statGroup}>
                    <div className={styles.statLabel}>
                        <span>Total Progress</span>
                        <span className={styles.statValue}>{formatTimeShort(totalMinutes)} / {formatTimeShort(totalRequirement)}</span>
                    </div>
                    <ProgressBar progress={totalPercent} size="small" color="secondary" />
                </div>

                <div className={styles.checklistSection}>
                    <div className={`${styles.checklistHeader} ${isCompletionEligible ? styles.headerComplete : styles.headerIncomplete}`}>
                        <ShieldCheck className={styles.icon} size={14} />
                        <h5>Completion Certificate Eligibility</h5>
                    </div>
                    <ul className={styles.checklist}>
                        <li className={totalMinutes >= totalRequirement ? styles.itemComplete : styles.itemIncomplete}>
                            <CheckCircle size={14} />
                            <span>Total Time (24h)</span>
                        </li>
                        <li className={progress?.overallProgress === 100 ? styles.itemComplete : styles.itemIncomplete}>
                            <CheckCircle size={14} />
                            <span>Curriculum Completed</span>
                        </li>
                        <li className={finalExamPassed ? styles.itemComplete : styles.itemIncomplete}>
                            <CheckCircle size={14} />
                            <span>Passed Final Exam</span>
                        </li>
                    </ul>
                </div>
            </section>
        </div>
    );
};

export default ComplianceSidebar;
